import { useState, useEffect, useCallback, useRef } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import {
  Loader2, FileText, FileDown, Images, Video, Download, Clapperboard, AlertTriangle, Mic, Music, Film, Users,
} from "lucide-react";
import { api, mediaUrl, downloadUrl } from "../lib/api";
import { SceneCard } from "../components/SceneCard";

export default function ProjectPage() {
  const { id } = useParams();
  const [project, setProject] = useState(null);
  const [error, setError] = useState(null);
  const [voiceover, setVoiceover] = useState(true);
  const [music, setMusic] = useState(true);
  const [characterVoices, setCharacterVoices] = useState(true);
  const [bulkProvider, setBulkProvider] = useState("pollo");
  const [imgProvider, setImgProvider] = useState("grok");
  const [transition, setTransition] = useState("fade");
  const [transitionDur, setTransitionDur] = useState("0.8");
  const pollRef = useRef(null);

  const fetchProject = useCallback(async () => {
    try {
      const { data } = await api.get(`/projects/${id}`);
      setProject(data);
      return data;
    } catch (e) {
      setError("Projet introuvable");
      return null;
    }
  }, [id]);

  useEffect(() => {
    fetchProject();
    pollRef.current = setInterval(async () => {
      const p = await fetchProject();
      if (!p) return clearInterval(pollRef.current);
      const busy =
        p.status === "generating" ||
        p.images_status === "generating" ||
        p.export_status === "rendering" ||
        p.clips_status === "generating" ||
        (p.scenes || []).some((s) => s.video_status === "generating" || s.image_status === "generating");
      if (!busy) clearInterval(pollRef.current);
    }, 4000);
    return () => clearInterval(pollRef.current);
  }, [fetchProject]);

  const restartPolling = () => {
    clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      const p = await fetchProject();
      if (!p) return clearInterval(pollRef.current);
      const busy =
        p.status === "generating" ||
        p.images_status === "generating" ||
        p.export_status === "rendering" ||
        p.clips_status === "generating" ||
        (p.scenes || []).some((s) => s.video_status === "generating" || s.image_status === "generating");
      if (!busy) clearInterval(pollRef.current);
    }, 4000);
  };

  const handleAnimateAll = async () => {
    const remaining = (project.scenes || []).filter((s) => s.image_url && !s.video_url).length;
    if (remaining === 0) return toast.info("Toutes les scènes ont déjà un clip");
    const label = bulkProvider === "pollo" ? "Pollo (vidéo + son)" : "Grok Imagine";
    if (!window.confirm(`Animer ${remaining} scène(s) avec ${label} ?\nChaque clip est facturé sur vos crédits ${bulkProvider === "pollo" ? "Pollo" : "xAI"} (~1-2 min par scène).`)) return;
    try {
      await api.post(`/projects/${id}/generate-clips`, { provider: bulkProvider, duration: 8 });
      toast.success(`Animation de toutes les scènes lancée (${label})`);
      setProject((p) => ({ ...p, clips_status: "generating" }));
      restartPolling();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Échec du lancement");
    }
  };

  const handleGenerateImages = async () => {
    try {
      await api.post(`/projects/${id}/generate-images`, { provider: imgProvider });
      toast.success(`Génération des images lancée (${imgProvider === "pollo" ? "Pollo" : "Grok"})`);
      setProject((p) => ({ ...p, images_status: "generating" }));
      restartPolling();
    } catch (e) {
      toast.error("Échec du lancement");
    }
  };

  const handleExport = async () => {
    try {
      await api.post(`/projects/${id}/export-video`, {
        voiceover, music, character_voices: characterVoices,
        transition, transition_duration: parseFloat(transitionDur),
      });
      toast.success("Assemblage du film lancé (voix off + musique)");
      setProject((p) => ({ ...p, export_status: "rendering", export_progress: "Préparation…" }));
      restartPolling();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Générez d'abord les images");
    }
  };

  if (error)
    return (
      <div className="max-w-3xl mx-auto px-6 py-24 text-center text-slate-400">
        <AlertTriangle className="w-10 h-10 mx-auto mb-4 text-rose-500" />
        {error}
      </div>
    );

  if (!project)
    return (
      <div className="flex items-center justify-center py-32">
        <Loader2 className="w-8 h-8 text-amber-500 animate-spin" />
      </div>
    );

  if (project.status === "generating")
    return (
      <div className="max-w-2xl mx-auto px-6 py-24 text-center" data-testid="script-generating-state">
        <div className="relative inline-block mb-8">
          <Clapperboard className="w-16 h-16 text-amber-500 animate-pulse" />
        </div>
        <h2 className="font-display text-2xl sm:text-3xl font-extrabold text-white mb-3">Grok écrit votre scénario…</h2>
        <p className="text-slate-400 mb-2">
          Écriture de {project.duration_min} minutes de film ({project.genre}) — cela peut prendre 1 à 3 minutes.
        </p>
        <p className="font-mono text-xs text-amber-400/70 tracking-widest">ACTES • SCÈNES • DIALOGUES • PROMPTS VISUELS</p>
        <div className="mt-8 h-1 w-64 mx-auto bg-white/10 rounded-full overflow-hidden">
          <div className="h-full w-1/3 bg-gradient-to-r from-amber-500 to-rose-500 rounded-full animate-[shimmer_1.5s_ease-in-out_infinite]" />
        </div>
      </div>
    );

  if (project.status === "error")
    return (
      <div className="max-w-2xl mx-auto px-6 py-24 text-center" data-testid="script-error-state">
        <AlertTriangle className="w-10 h-10 mx-auto mb-4 text-rose-500" />
        <h2 className="text-xl font-bold text-white mb-2">Échec de la génération du scénario</h2>
        <p className="text-sm text-slate-400 break-words">{project.error}</p>
      </div>
    );

  const scenes = project.scenes || [];
  const imagesDone = scenes.filter((s) => s.image_url).length;
  const clipsDone = scenes.filter((s) => s.video_url).length;
  let currentAct = null;

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
      <div className="mb-8">
        <p className="font-mono text-xs text-amber-400 tracking-widest uppercase mb-2">
          {project.genre} • {project.duration_min} MIN • {scenes.length} SCÈNES
        </p>
        <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl font-black tracking-tight text-white mb-3" data-testid="project-title">
          {project.title}
        </h1>
        <p className="text-slate-300 max-w-3xl italic">{project.logline}</p>
      </div>

      <div className="flex flex-wrap gap-3 mb-10 sticky top-16 z-30 bg-[#090B0E]/90 backdrop-blur-xl py-3 -mx-4 px-4 sm:-mx-6 sm:px-6 border-b border-white/5">
        <a
          href={downloadUrl(id, "txt")}
          data-testid="download-script-txt"
          className="flex items-center gap-2 px-4 py-2.5 rounded-xl border border-white/20 text-white text-sm font-semibold hover:bg-white/5 hover:border-amber-500/40 transition-all"
        >
          <FileText className="w-4 h-4" /> Script .TXT
        </a>
        <a
          href={downloadUrl(id, "pdf")}
          data-testid="download-script-pdf"
          className="flex items-center gap-2 px-4 py-2.5 rounded-xl border border-white/20 text-white text-sm font-semibold hover:bg-white/5 hover:border-amber-500/40 transition-all"
        >
          <FileDown className="w-4 h-4" /> Script .PDF
        </a>
        <div className="flex items-center rounded-xl border border-rose-500/40 overflow-hidden">
          <button
            data-testid="img-provider-grok"
            onClick={() => setImgProvider("grok")}
            className={`px-3 py-2.5 text-xs font-bold transition-colors ${
              imgProvider === "grok" ? "bg-rose-600 text-white" : "text-rose-400 hover:bg-rose-500/10"
            }`}
          >
            Img Grok
          </button>
          <button
            data-testid="img-provider-pollo"
            onClick={() => setImgProvider("pollo")}
            className={`px-3 py-2.5 text-xs font-bold transition-colors ${
              imgProvider === "pollo" ? "bg-rose-600 text-white" : "text-rose-400 hover:bg-rose-500/10"
            }`}
          >
            Img Pollo
          </button>
        </div>
        <button
          data-testid="generate-all-images-button"
          onClick={handleGenerateImages}
          disabled={project.images_status === "generating"}
          className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-rose-600 hover:bg-rose-500 text-white text-sm font-bold transition-all disabled:opacity-60"
        >
          {project.images_status === "generating" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Images className="w-4 h-4" />}
          {project.images_status === "generating"
            ? `Images ${imagesDone}/${scenes.length}…`
            : imagesDone > 0 ? "Compléter les images" : "Générer toutes les images"}
        </button>
        <div className="flex flex-wrap items-center gap-2">
          <button
            data-testid="toggle-voiceover"
            onClick={() => setVoiceover(!voiceover)}
            className={`flex items-center gap-1.5 px-3 py-2.5 rounded-xl text-xs font-bold border transition-all ${
              voiceover ? "bg-amber-500/15 text-amber-400 border-amber-500/50" : "text-slate-500 border-white/15"
            }`}
          >
            <Mic className="w-3.5 h-3.5" /> Voix off
          </button>
          <button
            data-testid="toggle-music"
            onClick={() => setMusic(!music)}
            className={`flex items-center gap-1.5 px-3 py-2.5 rounded-xl text-xs font-bold border transition-all ${
              music ? "bg-amber-500/15 text-amber-400 border-amber-500/50" : "text-slate-500 border-white/15"
            }`}
          >
            <Music className="w-3.5 h-3.5" /> Musique
          </button>
          <button
            data-testid="toggle-character-voices"
            onClick={() => setCharacterVoices(!characterVoices)}
            disabled={!voiceover}
            className={`flex items-center gap-1.5 px-3 py-2.5 rounded-xl text-xs font-bold border transition-all disabled:opacity-40 ${
              characterVoices && voiceover ? "bg-amber-500/15 text-amber-400 border-amber-500/50" : "text-slate-500 border-white/15"
            }`}
            title="Une voix différente pour chaque personnage dans les dialogues"
          >
            <Users className="w-3.5 h-3.5" /> Voix perso
          </button>
          <select
            data-testid="transition-select"
            value={transition}
            onChange={(e) => setTransition(e.target.value)}
            className="bg-[#12151B] border border-white/15 hover:border-amber-500/40 rounded-xl px-3 py-2.5 text-xs font-semibold text-slate-300 outline-none cursor-pointer"
            title="Style de transition entre les scènes"
          >
            <option value="fade">Fondu enchaîné</option>
            <option value="fadeblack">Fondu au noir</option>
            <option value="dissolve">Dissolution</option>
            <option value="wipeleft">Volet</option>
            <option value="slideleft">Glissement</option>
            <option value="circleopen">Cercle</option>
            <option value="none">Coupe sèche</option>
          </select>
          <select
            data-testid="transition-duration-select"
            value={transitionDur}
            onChange={(e) => setTransitionDur(e.target.value)}
            disabled={transition === "none"}
            className="bg-[#12151B] border border-white/15 hover:border-amber-500/40 rounded-xl px-3 py-2.5 text-xs font-semibold text-slate-300 outline-none cursor-pointer disabled:opacity-40"
            title="Durée de la transition"
          >
            <option value="0.5">0,5 s</option>
            <option value="0.8">0,8 s</option>
            <option value="1.2">1,2 s</option>
            <option value="2">2 s</option>
          </select>
        </div>
        <div className="flex items-center rounded-xl border border-sky-500/40 overflow-hidden">
          <button
            data-testid="bulk-provider-pollo"
            onClick={() => setBulkProvider("pollo")}
            className={`px-3 py-2.5 text-xs font-bold transition-colors ${
              bulkProvider === "pollo" ? "bg-sky-600 text-white" : "text-sky-400 hover:bg-sky-500/10"
            }`}
          >
            Pollo + son
          </button>
          <button
            data-testid="bulk-provider-grok"
            onClick={() => setBulkProvider("grok")}
            className={`px-3 py-2.5 text-xs font-bold transition-colors ${
              bulkProvider === "grok" ? "bg-sky-600 text-white" : "text-sky-400 hover:bg-sky-500/10"
            }`}
          >
            Grok
          </button>
        </div>
        <button
          data-testid="animate-all-scenes-button"
          onClick={handleAnimateAll}
          disabled={project.clips_status === "generating" || imagesDone === 0}
          className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-sky-600 hover:bg-sky-500 text-white text-sm font-bold transition-all disabled:opacity-60"
        >
          {project.clips_status === "generating" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Film className="w-4 h-4" />}
          {project.clips_status === "generating"
            ? `Animation ${clipsDone}/${scenes.length}…`
            : "Animer toutes les scènes"}
        </button>
        <button
          data-testid="export-video-button"
          onClick={handleExport}
          disabled={project.export_status === "rendering" || imagesDone === 0}
          className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-400 hover:to-amber-500 text-slate-950 text-sm font-bold shadow-[0_0_20px_rgba(245,158,11,0.25)] transition-all disabled:opacity-50"
        >
          {project.export_status === "rendering" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Video className="w-4 h-4" />}
          {project.export_status === "rendering"
            ? project.export_progress || "Assemblage en cours…"
            : "Assembler le film MP4"}
        </button>
      </div>

      {project.export_status === "done" && project.export_url && (
        <div className="mb-10 bg-[#12151B]/80 backdrop-blur-xl border border-amber-500/30 rounded-2xl p-5 sm:p-6" data-testid="export-result-section">
          <h2 className="font-display text-xl font-bold text-white mb-4 flex items-center gap-2">
            <Video className="w-5 h-5 text-amber-400" /> Votre film assemblé
          </h2>
          <p className="text-xs text-slate-400 mb-4">
            Le montage utilise les clips animés des scènes quand ils existent ({clipsDone}/{scenes.length} scènes animées), sinon l'image fixe avec effet de zoom. Animez toutes les scènes puis réassemblez pour un vrai rendu film.
          </p>
          <video controls className="w-full rounded-xl mb-4 max-h-[480px] bg-black" src={mediaUrl(project.export_url)} data-testid="export-video-player" />
          <a
            href={mediaUrl(project.export_url)}
            download
            data-testid="download-video-mp4"
            className="inline-flex items-center gap-2 bg-gradient-to-r from-amber-500 to-amber-600 text-slate-950 font-bold px-5 py-2.5 rounded-xl text-sm hover:from-amber-400 hover:to-amber-500"
          >
            <Download className="w-4 h-4" /> Télécharger le MP4
          </a>
        </div>
      )}
      {project.export_status === "error" && (
        <div className="mb-10 border border-rose-500/40 bg-rose-500/10 rounded-xl p-4 text-sm text-rose-300" data-testid="export-error">
          Échec de l'assemblage : {project.export_error}
        </div>
      )}

      <div className="space-y-6">
        {scenes.map((scene, i) => {
          const showAct = scene.act !== currentAct;
          currentAct = scene.act;
          const actInfo = (project.acts || []).find((a) => a.act === scene.act) || {};
          return (
            <div key={scene.index}>
              {showAct && (
                <h2 className="font-display text-2xl sm:text-3xl font-extrabold text-white mt-10 mb-5 flex items-center gap-3">
                  <span className="text-amber-500 font-mono text-sm tracking-widest">ACTE {scene.act}</span>
                  {actInfo.title}
                </h2>
              )}
              <SceneCard scene={scene} index={i} projectId={id} imgProvider={imgProvider} onUpdated={fetchProject} onVideoStarted={restartPolling} />
            </div>
          );
        })}
      </div>
    </div>
  );
}
